import { asyncTimeout } from '@caresend/utils';
import { defineComponent, ref, watch, computed, onMounted } from 'vue';

const IFRAME_ID = 'dynamicCopyEmbed';
var IFRAME_MESSAGE_EVENT;
(function (IFRAME_MESSAGE_EVENT) {
    IFRAME_MESSAGE_EVENT["SET_HEIGHT"] = "setHeight";
})(IFRAME_MESSAGE_EVENT || (IFRAME_MESSAGE_EVENT = {}));
const callBackEventTypes = {
    [IFRAME_MESSAGE_EVENT.SET_HEIGHT]: 'number',
};
const isIframeMessageEvent = (input) => typeof input === 'string' && Object.values(IFRAME_MESSAGE_EVENT).includes(input);
const getMessageTriggers = (callbacks) => async (e) => {
    var _a;
    /** Check if item is iterable */
    if (!((_a = e === null || e === void 0 ? void 0 : e.data) === null || _a === void 0 ? void 0 : _a[Symbol.iterator]))
        return;
    const [eventName, value] = e === null || e === void 0 ? void 0 : e.data;
    if (!isIframeMessageEvent(eventName))
        return;
    if (callbacks[eventName]) {
        const expectedType = callBackEventTypes[eventName];
        // eslint-disable-next-line valid-typeof
        if (typeof value !== expectedType)
            return;
        callbacks[eventName](value);
    }
};
const isIframe = (input) => input !== null && input.tagName === 'IFRAME';
const getIframe = () => {
    const iframe = document.getElementById(IFRAME_ID);
    if (!isIframe(iframe))
        return null;
    return iframe;
};
const postWidthMessageToIframe = () => {
    var _a;
    const iframe = getIframe();
    if (!iframe)
        return;
    (_a = iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.postMessage(['windowWidth', window.innerWidth], '*');
};
const updateIframeHeight = (height) => {
    const iframe = getIframe();
    if (!iframe)
        return;
    iframe.height = `${height}px`;
};
var script = defineComponent({
    name: 'DynamicCopyEmbed',
    props: {
        embed: {
            type: Object,
            required: true,
        },
    },
    setup(props) {
        const iframeVisible = ref(false);
        const currentIframeHeight = ref(0);
        watch(currentIframeHeight, updateIframeHeight, { immediate: true });
        const setIframeHeight = async (height) => {
            currentIframeHeight.value = height;
            /** Allow grace period for content within iframe to load to trigger another event */
            await asyncTimeout(50);
            iframeVisible.value = true;
            postWidthMessageToIframe();
        };
        const classNames = computed(() => {
            const classes = [];
            if (props.embed.settings) {
                const { dropShadow } = props.embed.settings;
                if (dropShadow) {
                    classes.push('DynamicCopyEmbed--drop-shadow');
                }
            }
            return classes;
        });
        const style = computed(() => ({
            opacity: iframeVisible.value ? '1' : '0',
        }));
        const containerStyle = computed(() => {
            if (!props.embed.settings)
                return {};
            const { backgroundColor, padding, borderRadius } = props.embed.settings;
            return {
                borderRadius: borderRadius ? `${borderRadius}px` : undefined,
                backgroundColor,
                padding,
            };
        });
        onMounted(() => {
            window.addEventListener('message', getMessageTriggers({
                [IFRAME_MESSAGE_EVENT.SET_HEIGHT]: setIframeHeight,
            }));
            window.addEventListener('resize', postWidthMessageToIframe);
        });
        return {
            IFRAME_ID,
            classNames,
            containerStyle,
            postWidthMessageToIframe,
            style,
        };
    },
});

export { script as default };
